import React from 'react';
import {MenuItem, ListSubheader} from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select';
import { InputField, MoneyLikeTextFieldProps } from '@/pages/components/common/InputField';
import {CURRENCIES, getTopCurrenciesFirst, TOP_CURRENCY_CODES, CurrencyItem} from '@/pages/components/common/currencyData';

export interface CurrencySelectProps
    extends Omit<MoneyLikeTextFieldProps, 'children' | 'value' | 'onChange' | 'label'> {
    value?: string; // code only
    onChange?: (code: string) => void;
    label?: React.ReactNode;
    includeListSubheaders?: boolean;
    /** When true, empty value stays empty (no HKD fallback) and an empty MenuItem is shown. */
    allowEmpty?: boolean;
}

const displayOf = (c: CurrencyItem) => `${c.code} ${c.symbol} — ${c.name}`;

export function CurrencySelect(props: CurrencySelectProps) {
    const {
        value,
        onChange,
        label = 'Currency',
        includeListSubheaders = true,
        allowEmpty = false,
        ...rest
    } = props;

    const items = React.useMemo(() => getTopCurrenciesFirst(), []);

    // Fallback to HKD when value undefined or not found (unless allowEmpty)
    const currentValue = React.useMemo(() => {
        if (value && CURRENCIES.some(c => c.code === value)) return value;
        return allowEmpty ? '' : 'HKD';
    }, [value, allowEmpty]);

    const handleChange = (e: SelectChangeEvent<string>) => {
        const code = e.target.value as string;
        onChange?.(code);
    };

    const topSet = React.useMemo(() => new Set(TOP_CURRENCY_CODES), []);

    return (
        <InputField
            select
            align='left'
            label={label}
            value={currentValue}
            onChange={handleChange as any}
            SelectProps={allowEmpty ? {displayEmpty: true} : undefined}
            {...rest}
        >
            {allowEmpty && (
                <MenuItem dense value="">
                    <em>—</em>
                </MenuItem>
            )}
            {includeListSubheaders && (
                <ListSubheader color='primary' disableSticky>Popular Currency</ListSubheader>
            )}
            {items.filter(c => topSet.has(c.code)).map((c) => (
                <MenuItem dense key={c.code} value={c.code}>{displayOf(c)}</MenuItem>
            ))}
            {includeListSubheaders && (
                <ListSubheader color='primary' disableSticky>ALL Currency</ListSubheader>
            )}
            {items.filter(c => !topSet.has(c.code)).map((c) => (
                <MenuItem dense key={c.code} value={c.code}>{displayOf(c)}</MenuItem>
            ))}
        </InputField>
    );
}
